import { ComboBox, DatePicker, DefaultButton, IComboBoxOption, Panel, PanelType, PrimaryButton, Separator, Spinner, SpinnerSize, Stack } from '@fluentui/react';
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { getParticipantOptions } from '../../../services/API/ParticipantService';
import { AccessTokens } from '../../../services/API/BaseService';
import { StatisticsPeriod, StatisticState, TransferDirection } from '../types/BusiessDocumentTypes';
import { exportBusinessStatisticsReport } from '../../../services/API/StatisticsService';

type ExportDataPanelProps = {
  period: StatisticsPeriod;
  accessTokens: AccessTokens;
  isOpen: boolean;
  onDismiss: () => void;
};

const defaultOption: IComboBoxOption = { key: '', text: '' };

const stateOptions = [
  { key: -1, text: '' },
  { key: StatisticState.Sent, text: 'Sent' },
  { key: StatisticState.Received, text: 'Received' },
  { key: StatisticState.SendFailed, text: 'Send Failed' },
  { key: StatisticState.ReceiveFailed, text: 'Receive Failed' },
] as IComboBoxOption[];

const directionOptions = [
  { key: -1, text: '' },
  { key: TransferDirection.BvgToBvg, text: 'BVG → BVG' },
  { key: TransferDirection.BvgToEasx, text: 'BVG → EASX' },
  { key: TransferDirection.EasxToBvg, text: 'EASX → BVG' },
  { key: TransferDirection.EasxToEasx, text: 'EASX → EASX' },
] as IComboBoxOption[];

export const ExportDataPanel: React.FC<ExportDataPanelProps> = ({ period, onDismiss, isOpen, accessTokens }) => {
  const [participantOptions, setParticipantOptions] = useState<IComboBoxOption[]>([]);
  const [receiver, setReceiver] = useState<string | undefined>('');
  const [sender, setSender] = useState<string | undefined>('');
  const [state, setState] = useState<number | undefined>(-1);
  const [direction, setDirection] = useState<number | undefined>(-1);
  const [fromDate, setFromDate] = useState<Date | undefined>(undefined);
  const [toDate, setToDate] = useState<Date | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [resetKey, setResetKey] = React.useState(0);
  const periodName = useMemo(() => StatisticsPeriod[period].toLowerCase(), [period]);

  const onExportClick = () => {
    setIsLoading(true);
    exportBusinessStatisticsReport(accessTokens, periodName, {
      period,
      receiver,
      sender,
      state: state === -1 ? undefined : state,
      direction: direction === -1 ? undefined : direction,
      fromDate,
      toDate,
    }).finally(() => setIsLoading(false));
  };

  useEffect(() => {
    getParticipantOptions(accessTokens).then((data) => setParticipantOptions([...[defaultOption], ...data]));
  }, [accessTokens]);

  const handleFromDateChange = (date?: Date | null) => {
    setFromDate(date ?? undefined);

    if (date && toDate && toDate < date) {
      setToDate(undefined);
      setResetKey((prev) => prev + 1);
    }
  };

  const handleToDateChange = (date?: Date | null) => {
    if (date && fromDate && date < fromDate) {
      toast.error("The 'To Date' cannot be earlier than the 'From Date'");
      setToDate(undefined);
      setResetKey((prev) => prev + 1);
      return;
    }
    setToDate(date ?? undefined);
  };

  return (
    <Panel
      type={PanelType.smallFixedFar}
      headerText={`Export ${periodName} transfer data`}
      onDismiss={onDismiss}
      isOpen={isOpen}
      closeButtonAriaLabel="Close"
      onRenderFooterContent={() => (
        <>
          <Separator />
          <Stack horizontal tokens={{ childrenGap: 10 }}>
            <PrimaryButton disabled={isLoading} onClick={onExportClick}>
              {isLoading ? (
                <>
                  <Spinner size={SpinnerSize.xSmall} styles={{ root: { marginRight: 8 } }} />
                  Preparing…
                </>
              ) : (
                'Export'
              )}
            </PrimaryButton>{' '}
            <DefaultButton onClick={onDismiss}>Cancel</DefaultButton>
          </Stack>
        </>
      )}
    >
      <DatePicker label="From Date" value={fromDate} onSelectDate={handleFromDateChange}></DatePicker>
      <DatePicker key={resetKey} label="To Date" value={toDate} onSelectDate={handleToDateChange}></DatePicker>
      <ComboBox label="Sender" allowFreeform={false} options={participantOptions} selectedKey={sender} onChange={(ev, option) => option && setSender(option.key as string)} />
      <ComboBox label="Receiver" allowFreeform={false} options={participantOptions} selectedKey={receiver} onChange={(ev, option) => option && setReceiver(option.key as string)} />
      <ComboBox label="State" allowFreeform={false} options={stateOptions} selectedKey={state} onChange={(ev, option) => option && setState(option.key as number)} />
      <ComboBox
        label="Direction"
        allowFreeform={false}
        options={directionOptions}
        selectedKey={direction}
        onChange={(ev, option) => option && setDirection(option.key as number)}
      />
    </Panel>
  );
};
